import { defineComponent as _defineComponent } from 'vue'
import { PropType, computed, inject, provide, ref } from "vue";
import { IBookableDatesInfo, IDateInfo } from "@/domain/entities/book";
import {
  BookIntervalCode,
  ErrorTypeCode,
  PROVIDE_KEY_GET_MONTHLY_BOOKABLE_DATES_WITH_SEAT,
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  RemoveLoadingImage,
  RouterName,
  ShowErrorModal,
  ShowLoadingImage,
} from "@/types";
import { useBookStore } from "@/stores/bookStore";
import { ILineFare } from "@/domain/entities/lineInfo";
import { IResultData } from "@/domain/entities/shared/result";
import Modal from "../shared/modal/base/Modal.vue";
import router from "@/router";
import SeatAndPayment from "./SeatAndPayment.vue";
import useGetAccessToken from "@/use/useGetAccessToken";
import { useRoute } from "vue-router";


const __default__ = {
  name: "MonthlyBookSeatAndPayment",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  turnId: {
    type: String,
    required: true,
  },
  getOnBusStopId: {
    type: String,
    required: true,
  },
  getOffBusStopId: {
    type: String,
    required: true,
  },
  lineFare: {
    type: Object as PropType<ILineFare>,
    required: true,
  },
  seatsSelectable: {
    type: Boolean,
    default: false,
  },
  currentBookInterval: {
    type: String as PropType<BookIntervalCode>,
  },
},
  emits: ["toggleShowBookDetail"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;

const emit = __emit;

const route = useRoute();
const bookStore = useBookStore();
const isLoggedIn = computed(() => !!useGetAccessToken());

const emitToggleShowBookDetail = (show: boolean) => {
  emit("toggleShowBookDetail", show);
};

const handleError = (title: string, errorMessage?: string, result?: any) => {
  showErrorModal(
    title,
    errorMessage ?? result?.errorMessage,
    result?.resultCode,
    result?.errorTime
  );
};

const showLoginRequriedAlertForBook = ref(false);

// 요금 정보가 유효한지 확인
const isValidLineFare = (lineFare: ILineFare) => {
  return lineFare.dailyFare !== undefined && lineFare.monthlyFare !== undefined;
};

/** 월예약 가능일 조회 */
const bookingDates = ref<IDateInfo[]>([]);
const getMonthlyBookableDates = async (seatNo?: string) => {
  const bookableDatesResult = await bookStore.getBookableDates(
    props.turnId,
    props.getOnBusStopId,
    props.getOffBusStopId,
    BookIntervalCode.MONTH,
    props.lineFare,
    seatNo
  );

  return bookableDatesResult;
};

const proceedMonthlyBook = async () => {
  if (!isLoggedIn.value) {
    showLoginRequriedAlertForBook.value = true;
    return;
  }

  showLoadingImage();

  if (!isValidLineFare(props.lineFare)) {
    handleError(
      "요금조회 오류",
      "요금정보를 확인할 수 없어 월예약을 진행할 수 없습니다."
    );
  }

  const bookableDatesResult = await getMonthlyBookableDates();

  removeLoadingImage();

  const targetBookableDates = bookableDatesResult?.data?.[0];
  bookingDates.value = targetBookableDates
    ? bookStore.convertBookableDatesInfoToDateInfos(targetBookableDates)
    : [];

  handleMonthlyBookResult(bookableDatesResult);
};

const handleMonthlyBookResult = (
  bookableDatesResult: IResultData<IBookableDatesInfo[]>
) => {
  const isEmptyMonthlyBookableDates =
    bookableDatesResult.errorType ===
    ErrorTypeCode.EMPTY_MONTHLY_BOOKABLE_DATES;

  const isDailyBookCheaper =
    bookableDatesResult.errorType === ErrorTypeCode.MONTHLY_BOOK_UNAVAILABLE;

  const hasFullyBookedDate = bookStore.hasFullyBookedDate(
    bookableDatesResult.data ?? []
  );

  const hasMyBookedDate = bookableDatesResult.data?.[0].bookableDates.some(
    (date) => date.isBookedByMe
  );

  if (isEmptyMonthlyBookableDates) {
    toggleShowEmptyMonthlyBookableDatesModal(true);
  } else if (isDailyBookCheaper) {
    toggleShowMonthlyBookExpensiveModal(true);
  } else if (hasFullyBookedDate) {
    toggleShowHasFullyBookedDateModal(true);
  } else if (hasMyBookedDate) {
    toggleShowHasBookedDateModal(true);
  } else {
    toggleShowSeatAndPayment(true);
  }
};

/** 월예약시 모든 날짜에 특정 좌석에 대해 예약 가능여부 확인 */
const getMonthlyBookableDatesWithSeat = async () => {
  showLoadingImage();

  const bookableDatesResult = await getMonthlyBookableDates(
    selectedSeatNo.value
  );

  removeLoadingImage();

  return bookableDatesResult.data;
};
provide(
  PROVIDE_KEY_GET_MONTHLY_BOOKABLE_DATES_WITH_SEAT,
  getMonthlyBookableDatesWithSeat
);

/** 월예약 좌석 업데이트 */
const selectedSeatNo = ref("");
const updateSeatNumbers = (seatNo: string) => {
  selectedSeatNo.value = seatNo;
  bookingDates.value?.forEach((bookingDate) => {
    if (bookingDate.bookableDate) {
      bookingDate.bookableDate.mySeatNo = seatNo;
    }
  });
};

// 월예약 화면(좌석배치도, 결제버튼만 있는 화면) 처리
const shouldShowSeatAndPayment = ref(false);
const toggleShowSeatAndPayment = (show: boolean) => {
  if (show) {
    route.meta.onBackHandlerList.push(() => toggleShowSeatAndPayment(false));
  } else {
    route.meta.onBackHandlerList.pop();
  }
  shouldShowSeatAndPayment.value = show;
};

// 월예약의 예약가능한 일자가 없는 경우 월예약 불가 모달 처리
const shouldShowEmptyMonthlyBookableDatesModal = ref(false);
const toggleShowEmptyMonthlyBookableDatesModal = (show: boolean) => {
  shouldShowEmptyMonthlyBookableDatesModal.value = show;
};

// 월예약이 일예약보다 비씬 경우 월예약 불가 모달 처리
const shouldShowMonthlyBookExpensiveModal = ref(false);
const toggleShowMonthlyBookExpensiveModal = (show: boolean) => {
  shouldShowMonthlyBookExpensiveModal.value = show;
};

// 만석인 날짜가 있는 경우 월예약 불가 모달 처리
const shouldShowHasFullyBookedDateModal = ref(false);
const toggleShowHasFullyBookedDateModal = (show: boolean) => {
  shouldShowHasFullyBookedDateModal.value = show;
};

// 이미 예약한 날짜가 있는 경우 월예약 불가 모달 처리
const shouldShowHasBookedDateModal = ref(false);
const toggleShowHasBookedDateModal = (show: boolean) => {
  shouldShowHasBookedDateModal.value = show;
};

/** 로그인 페이지 이동 */
const goToLoginPage = () => {
  showLoginRequriedAlertForBook.value = false;
  router.push({ name: RouterName.Login });
};

__expose({ proceedMonthlyBook });

const __returned__ = { props, showErrorModal, showLoadingImage, removeLoadingImage, emit, route, bookStore, isLoggedIn, emitToggleShowBookDetail, handleError, showLoginRequriedAlertForBook, isValidLineFare, bookingDates, getMonthlyBookableDates, proceedMonthlyBook, handleMonthlyBookResult, getMonthlyBookableDatesWithSeat, selectedSeatNo, updateSeatNumbers, shouldShowSeatAndPayment, toggleShowSeatAndPayment, shouldShowEmptyMonthlyBookableDatesModal, toggleShowEmptyMonthlyBookableDatesModal, shouldShowMonthlyBookExpensiveModal, toggleShowMonthlyBookExpensiveModal, shouldShowHasFullyBookedDateModal, toggleShowHasFullyBookedDateModal, shouldShowHasBookedDateModal, toggleShowHasBookedDateModal, goToLoginPage, get BookIntervalCode() { return BookIntervalCode }, Modal, SeatAndPayment }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})